import React, { useEffect, useMemo, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { LoadingDiv, SweetAlertShow } from "../master/Master";
import Master from "./Master";
import { staffAttendanceListMonthly } from "../../api/Attendance";
import { formatToDate, formatToTime } from "../master/FormatToDateTime";

const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const safeDate = (value) => {
    if (!value) return "";
    try {
        return formatToDate(value);
    } catch {
        return String(value);
    }
};

const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const raw = String(value).trim();
    // Prefer the ISO date portion when the API returns a timestamp.
    const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) {
        const date = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
        return Number.isNaN(date.getTime()) ? null : date;
    }

    let formatted = raw;
    try {
        formatted = String(formatToDate(value) || raw).trim();
    } catch {
        // Keep the original value if formatting fails.
    }

    const parts = formatted.match(/^(\d{1,2})[\/.-](\d{1,2})[\/.-](\d{4})$/);
    if (parts) {
        // formatToDate in this project normally returns day/month/year.
        const first = Number(parts[1]);
        const second = Number(parts[2]);
        const day = first > 12 ? first : second > 12 ? second : first;
        const month = first > 12 ? second : second > 12 ? first : second;
        const date = new Date(Number(parts[3]), month - 1, day);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const fallback = new Date(raw);
    if (Number.isNaN(fallback.getTime())) return null;
    return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
};

const getInitials = (name) =>
    String(name || "?")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");

export default function StaffAttendancesMonthlyV2() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [search, setSearch] = useState("");
    const [attendanceView, setAttendanceView] = useState("all");
    const [filters, setFilters] = useState({ month: getCurrentMonth() });

    const [yearString, monthString] = (filters.month || getCurrentMonth()).split("-");
    const year = Number(yearString);
    const month = Number(monthString);
    const totalDays = new Date(year, month, 0).getDate();

    const today = useMemo(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    const days = useMemo(
        () => Array.from({ length: totalDays }, (_, index) => index + 1),
        [totalDays]
    );

    const monthTitle = useMemo(
        () => new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
            month: "long",
            year: "numeric",
        }),
        [year, month]
    );

    const getAttendanceList = async () => {
        setLoading(true);
        const addUrl = filters.month ? `&month=${encodeURIComponent(filters.month)}` : "";

        try {
            const response = await staffAttendanceListMonthly(addUrl);
            if (response?.data?.status) {
                const payload = response?.data?.data;
                setData(Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []);
            } else {
                setData([]);
                SweetAlertShow("info", "Information", response?.data?.message || "No attendance data found.");
            }
        } catch (error) {
            setData([]);
            SweetAlertShow(
                "error",
                "Error",
                error?.response?.data?.message ?? error?.message ?? "Unable to load monthly staff attendance."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAttendanceList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.month]);

    const staffList = data;

    const monthDayInfo = useMemo(() => {
        const result = {};
        days.forEach((day) => {
            const dateObject = new Date(year, month - 1, day);
            dateObject.setHours(0, 0, 0, 0);
            const isPastOrToday = dateObject <= today;
            const dateString = `${filters.month}-${String(day).padStart(2, "0")}`;
            const formattedDate = safeDate(dateString);
            const anyAttendance = staffList.some((staff) =>
                Array.isArray(staff?.attendances) &&
                staff.attendances.some((attendance) => safeDate(attendance?.date) === formattedDate)
            );

            result[day] = {
                dateObject,
                dateString,
                formattedDate,
                isPastOrToday,
                // Preserve the previous report's convention for dates with no staff attendance.
                isHoliday: isPastOrToday && !anyAttendance,
            };
        });
        return result;
    }, [days, filters.month, staffList, today, year, month]);

    const getDayInfo = (staff, day) => {
        const info = monthDayInfo[day];
        const attendances = (Array.isArray(staff?.attendances) ? staff.attendances : [])
            .filter((attendance) => safeDate(attendance?.date) === info?.formattedDate);

        const joiningDate = parseDate(staff?.employment_latest?.joining_date ?? staff?.created_at);
        const isAdmitted = !joiningDate || !info?.dateObject || info.dateObject >= joiningDate;
        const workingDaysRaw =
            staff?.employment_latest?.working_days ??
            staff?.employment_latest?.working_schedule?.working_days;

        let workingDays = workingDaysRaw;
        try {
            if (typeof workingDays === "string") workingDays = JSON.parse(workingDays);
        } catch {
            workingDays = null;
        }
        if (!Array.isArray(workingDays)) {
            workingDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        }

        const weekday = info?.dateObject?.toLocaleDateString("en-US", { weekday: "short" });
        const hasPresent = attendances.some((item) => Number(item?.check_in) === 1);
        const hasAbsent = attendances.some((item) => Number(item?.check_in) === 2);
        const hasAttendance = attendances.length > 0;
        const isHoliday = isAdmitted && info?.isPastOrToday && !hasAttendance &&
            (!workingDays.includes(weekday) || info?.isHoliday);

        let status = "future";
        if (!isAdmitted) status = "na";
        else if (hasPresent) status = "present";
        else if (hasAbsent) status = "absent";
        else if (hasAttendance) status = "not_marked";
        else if (isHoliday) status = "holiday";
        else if (info?.isPastOrToday) status = "not_marked";

        return {
            ...info,
            attendances,
            hasAttendance,
            hasPresent,
            hasAbsent,
            isAdmitted,
            isHoliday,
            status,
        };
    };

    const analyseStaff = (staff) => {
        let present = 0;
        let absent = 0;
        let notMarked = 0;
        let workingDays = 0;
        let holidays = 0;
        let dayStatuses = {};

        days.forEach((day) => {
            const info = getDayInfo(staff, day);

            if (info.status === "present") {
                present += 1;
                if (info.isPastOrToday && !info.isHoliday) workingDays += 1;
            } else if (info.status === "absent") {
                absent += 1;
                if (info.isPastOrToday && !info.isHoliday) workingDays += 1;
            } else if (info.status === "not_marked" && info.isPastOrToday && info.isAdmitted) {
                notMarked += 1;
                workingDays += 1;
            } else if (info.status === "holiday") {
                holidays += 1;
            }

            dayStatuses[day] = info;
        });

        const percentage = workingDays > 0
            ? Number(((present / workingDays) * 100).toFixed(1))
            : 0;

        return { staff, present, absent, notMarked, workingDays, holidays, percentage, dayStatuses };
    };

    const analysedStaff = useMemo(
        () => staffList.map(analyseStaff),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [staffList, monthDayInfo, filters.month]
    );

    const filteredStaff = useMemo(() => {
        const term = search.trim().toLowerCase();

        return analysedStaff.filter((row) => {
            const staff = row.staff;
            const searchable = [
                staff?.name,
                staff?.gender,
                staff?.mobile,
                staff?.phone,
                staff?.staff_code,
                staff?.employee_id,
                staff?.employment_latest?.designation?.name,
            ].filter(Boolean).join(" ").toLowerCase();

            if (term && !searchable.includes(term)) return false;
            if (attendanceView === "low") return row.workingDays > 0 && row.percentage < 75;
            if (attendanceView === "absent") return row.absent > 0;
            if (attendanceView === "not_marked") return row.notMarked > 0;
            if (attendanceView === "perfect") return row.workingDays > 0 && row.percentage >= 100;
            return true;
        });
    }, [analysedStaff, search, attendanceView]);

    const summary = useMemo(() => {
        const totals = filteredStaff.reduce((acc, row) => {
            acc.present += row.present;
            acc.absent += row.absent;
            acc.notMarked += row.notMarked;
            acc.workingDays += row.workingDays;
            return acc;
        }, { present: 0, absent: 0, notMarked: 0, workingDays: 0 });

        return {
            staff: filteredStaff.length,
            ...totals,
            percentage: totals.workingDays
                ? Number(((totals.present / totals.workingDays) * 100).toFixed(1))
                : 0,
        };
    }, [filteredStaff]);

    const renderDayCell = (info) => {
        if (!info) return <span className="staff-future">—</span>;

        if (info.status === "present") {
            const attendance = info.attendances.find((item) => Number(item?.check_in) === 1) || info.attendances[0];
            const inTime = attendance?.check_in_time;
            const outTime = attendance?.check_out_time;
            return (
                <div className="staff-day-times">
                    {inTime ? <span className="staff-in-time">{formatToTime(inTime)}</span> : <span className="staff-status present">P</span>}
                    {outTime && <span className="staff-out-time">{formatToTime(outTime)}</span>}
                </div>
            );
        }

        if (info.status === "absent") return <span className="staff-status absent">A</span>;
        if (info.status === "holiday") return <span className="staff-status holiday">H</span>;
        if (info.status === "na") return <span className="staff-status na">NA</span>;
        if (info.status === "not_marked") return <span className="staff-status not-marked">N</span>;
        return <span className="staff-future">—</span>;
    };

    const escapeHtml = (value) =>
        String(value ?? "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const handlePrint = () => {
        if (!filteredStaff.length) {
            SweetAlertShow("info", "Information", "There is no staff attendance to print.");
            return;
        }

        // Print in a separate, clean document so application/sidebar CSS cannot
        // squeeze the register or hide parts of the table.
        const printWindow = window.open("", "_blank", "width=1600,height=1000");
        if (!printWindow) {
            SweetAlertShow("info", "Popup Blocked", "Please allow popups for this website and try again.");
            return;
        }

        const dayHeaders = days.map((day) => {
            const weekday = monthDayInfo[day]?.dateObject
                ?.toLocaleDateString("en-IN", { weekday: "short" })?.slice(0, 2) || "";
            return `<th class="day ${monthDayInfo[day]?.isHoliday ? "holiday-day" : ""}">
                <strong>${day}</strong><small>${escapeHtml(weekday)}</small>
            </th>`;
        }).join("");

        const bodyRows = filteredStaff.map((row, index) => {
            const staff = row.staff;
            const dayCells = days.map((day) => {
                const info = row.dayStatuses[day];
                let value = "";
                let css = "";

                if (info?.status === "present") {
                    const attendance = info.attendances?.find((item) => Number(item?.check_in) === 1)
                        || info.attendances?.[0];
                    const inTime = attendance?.check_in_time ? formatToTime(attendance.check_in_time) : "";
                    const outTime = attendance?.check_out_time ? formatToTime(attendance.check_out_time) : "";
                    value = inTime && outTime ? `${inTime}<br>${outTime}` : inTime || outTime || "P";
                    css = "present";
                } else if (info?.status === "absent") {
                    value = "A"; css = "absent";
                } else if (info?.status === "holiday") {
                    value = "H"; css = "holiday";
                } else if (info?.status === "na") {
                    value = "NA"; css = "na";
                } else if (info?.status === "not_marked") {
                    value = "N"; css = "not-marked";
                } else {
                    value = "—"; css = "future";
                }

                return `<td class="day-cell ${css}">${value}</td>`;
            }).join("");

            return `<tr>
                <td class="serial">${index + 1}</td>
                <td class="staff-name">
                    <strong>${escapeHtml(staff?.name || "—")}</strong>
                    <small>${escapeHtml(staff?.staff_code || staff?.employee_id || staff?.employment_latest?.designation?.name || "")}</small>
                </td>
                ${dayCells}
                <td class="total present">${row.present}</td>
                <td class="total absent">${row.absent}</td>
                <td class="total not-marked">${row.notMarked}</td>
                <td class="total">${row.workingDays}</td>
                <td class="total">${row.percentage}%</td>
            </tr>`;
        }).join("");

        const dailyTotals = days.map((day) => {
            const dayRows = filteredStaff.map((row) => row.dayStatuses[day]);
            const present = dayRows.filter((info) => info?.status === "present").length;
            const absent = dayRows.filter((info) => info?.status === "absent").length;
            const notMarked = dayRows.filter((info) => info?.status === "not_marked").length;
            return `<td class="day-total"><b>${present}</b><small>P</small><b>${absent}</b><small>A</small><b>${notMarked}</b><small>N</small></td>`;
        }).join("");

        const overallPresent = filteredStaff.reduce((sum, row) => sum + row.present, 0);
        const overallAbsent = filteredStaff.reduce((sum, row) => sum + row.absent, 0);
        const overallNotMarked = filteredStaff.reduce((sum, row) => sum + row.notMarked, 0);
        const overallWorkingDays = filteredStaff.reduce((sum, row) => sum + row.workingDays, 0);
        const overallPercentage = overallWorkingDays
            ? Number(((overallPresent / overallWorkingDays) * 100).toFixed(1))
            : 0;

        printWindow.document.open();
        printWindow.document.write(`<!doctype html>
<html>
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Staff Monthly Attendance - ${escapeHtml(monthTitle)}</title>
<style>
    @page { size: A3 landscape; margin: 6mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; width: 100%; color: #111; background: #fff; font-family: Arial, Helvetica, sans-serif; }
    body { font-size: 7px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .header { text-align: center; border-bottom: 1.5px solid #222; padding: 0 0 5px; margin-bottom: 6px; }
    .header h1 { font-size: 15px; margin: 0 0 3px; }
    .header p { font-size: 10px; margin: 0 0 3px; font-weight: bold; }
    .header small { font-size: 7px; }
    .summary { display: grid; grid-template-columns: repeat(5, 1fr); gap: 4px; margin-bottom: 6px; }
    .summary div { border: 1px solid #aaa; padding: 4px; text-align: center; }
    .summary span { display: block; font-size: 6px; color: #555; text-transform: uppercase; }
    .summary strong { display: block; margin-top: 2px; font-size: 10px; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    thead { display: table-header-group; }
    tfoot { display: table-row-group; }
    tr { break-inside: avoid; page-break-inside: avoid; }
    th, td { border: 1px solid #666; padding: 2px 1px; text-align: center; vertical-align: middle; overflow-wrap: anywhere; line-height: 1.1; }
    th { background: #e9edf3; font-size: 6.5px; }
    th.day { width: ${Math.max(6.2, Math.min(8.2, 330 / days.length))}mm; }
    th.day strong, th.day small { display: block; }
    th.day strong { font-size: 7px; }
    th.day small { font-size: 5.5px; font-weight: normal; margin-top: 1px; }
    .serial { width: 6mm; }
    .staff-name { width: 32mm; text-align: left; padding-left: 3px; }
    .staff-name strong, .staff-name small { display: block; }
    .staff-name strong { font-size: 7px; }
    .staff-name small { margin-top: 2px; font-size: 5.5px; color: #444; }
    .total { width: 9mm; font-size: 6.5px; font-weight: bold; }
    .present { color: #087b42; font-weight: bold; }
    .absent { color: #bf2424; font-weight: bold; }
    .not-marked { color: #a46100; font-weight: bold; }
    .holiday, .na { color: #555; font-weight: bold; }
    .future { color: #888; }
    .day-cell { font-size: 6px; }
    .day-total { background: #f1f1f1; white-space: nowrap; font-size: 5.5px; }
    .day-total b { font-size: 6px; }
    .day-total small { font-size: 4.5px; margin-right: 1px; }
    .grand-total th, .grand-total td { background: #e5e5e5; font-weight: bold; }
    .footer { display: flex; justify-content: space-between; gap: 10px; border-top: 1px solid #999; margin-top: 8px; padding-top: 5px; font-size: 7px; }
    @media screen { body { padding: 12px; } }
</style>
</head>
<body>
    <div class="header">
        <h1>Monthly Staff Attendance Register</h1>
        <p>${escapeHtml(monthTitle)}</p>
        <small>P: Present · A: Absent · H: Holiday / Non-working day · NA: Before joining · N: Not Marked · In/Out times shown where available</small>
    </div>
    <div class="summary">
        <div><span>Staff Members</span><strong>${filteredStaff.length}</strong></div>
        <div><span>Present Marks</span><strong>${overallPresent}</strong></div>
        <div><span>Absent Marks</span><strong>${overallAbsent}</strong></div>
        <div><span>Not Marked</span><strong>${overallNotMarked}</strong></div>
        <div><span>Attendance Average</span><strong>${overallPercentage}%</strong></div>
    </div>
    <table>
        <thead><tr>
            <th class="serial">#</th>
            <th class="staff-name">Staff Name / Code</th>
            ${dayHeaders}
            <th class="total">P</th><th class="total">A</th><th class="total">N</th><th class="total">Working</th><th class="total">%</th>
        </tr></thead>
        <tbody>${bodyRows}</tbody>
        <tfoot><tr class="grand-total">
            <th colspan="2">MONTHLY TOTAL</th>
            ${dailyTotals}
            <td class="total">${overallPresent}</td>
            <td class="total">${overallAbsent}</td>
            <td class="total">${overallNotMarked}</td>
            <td class="total">${overallWorkingDays}</td>
            <td class="total">${overallPercentage}%</td>
        </tr></tfoot>
    </table>
    <div class="footer">
        <span>Generated: ${escapeHtml(new Date().toLocaleString("en-IN"))}</span>
        <span>Staff Signature: ____________________ &nbsp;&nbsp;&nbsp; Principal Signature: ____________________</span>
    </div>
<script>
    window.onload = function () {
        setTimeout(function () { window.focus(); window.print(); }, 500);
    };
    window.onafterprint = function () { window.close(); };
</script>
</body>
</html>`);
        printWindow.document.close();
    };

    const resetFilters = () => {
        setSearch("");
        setAttendanceView("all");
        setFilters({ month: getCurrentMonth() });
    };

    return (
        <>
            {loading && <LoadingDiv />}
            <div className="layout-wrapper layout-content-navbar staff-monthly-page">
                <div className="layout-container">
                    <div className="staff-no-print"><Header /></div>
                    <div className="layout-page">
                        <div className="staff-no-print"><Nav /></div>
                        <div className="content-wrapper">
                            <div className="container-fluid flex-grow-1 container-p-y staff-monthly-container">
                                <div className="staff-no-print"><Master /></div>

                                <main className="staff-monthly-main">
                                    <section className="staff-monthly-header staff-no-print">
                                        <div className="staff-monthly-heading">
                                            <div className="staff-monthly-icon"><i className="ri-calendar-2-line" /></div>
                                            <div>
                                                <div className="staff-breadcrumb">Attendance Reports <i className="ri-arrow-right-s-line" /> Monthly Register</div>
                                                <h1>Monthly Staff Attendance Register</h1>
                                                <p>Staff-wise daily attendance, check-in/out times and monthly totals in one register.</p>
                                            </div>
                                        </div>
                                        <div className="staff-header-actions">
                                            <button className="staff-action secondary" type="button" onClick={getAttendanceList} disabled={loading}>
                                                <i className="ri-refresh-line" /> Refresh
                                            </button>
                                            <button className="staff-action primary" type="button" onClick={handlePrint} disabled={!filteredStaff.length}>
                                                <i className="ri-printer-line" /> Print Register
                                            </button>
                                        </div>
                                    </section>

                                    <section className="staff-filter-card staff-no-print">
                                        <div className="staff-section-title">
                                            <div><h2><i className="ri-filter-3-line" /> Monthly Report Filters</h2><p>Select a month and quickly find staff attendance.</p></div>
                                            <button className="staff-reset" type="button" onClick={resetFilters}><i className="ri-restart-line" /> Reset</button>
                                        </div>
                                        <div className="staff-filter-grid">
                                            <label className="staff-field">
                                                <span>Attendance Month</span>
                                                <div className="staff-input-wrap"><i className="ri-calendar-line" /><input type="month" value={filters.month} onChange={(e) => setFilters({ month: e.target.value || getCurrentMonth() })} /></div>
                                            </label>
                                            <div className="staff-filter-note"><i className="ri-information-line" /><span>Selected report: <strong>{monthTitle}</strong></span></div>
                                        </div>
                                    </section>

                                    <section className="staff-summary-grid">
                                        <article className="staff-summary-card blue"><div className="staff-summary-top"><span><i className="ri-team-line" /></span><small>Staff Members</small></div><strong>{summary.staff}</strong><p>Staff in current view</p></article>
                                        <article className="staff-summary-card green"><div className="staff-summary-top"><span><i className="ri-checkbox-circle-line" /></span><small>Present</small></div><strong>{summary.present}</strong><p>Present marks</p></article>
                                        <article className="staff-summary-card red"><div className="staff-summary-top"><span><i className="ri-close-circle-line" /></span><small>Absent</small></div><strong>{summary.absent}</strong><p>Absent marks</p></article>
                                        <article className="staff-summary-card amber"><div className="staff-summary-top"><span><i className="ri-time-line" /></span><small>Not Marked</small></div><strong>{summary.notMarked}</strong><p>Pending attendance marks</p></article>
                                        <article className="staff-summary-card violet"><div className="staff-summary-top"><span><i className="ri-percent-line" /></span><small>Attendance Average</small></div><strong>{summary.percentage}%</strong><p>{summary.workingDays} working-day records</p></article>
                                    </section>

                                    <section className="staff-register-card">
                                        <div className="staff-table-heading staff-no-print">
                                            <div><h2>Staff Attendance Register</h2><p>Showing <strong>{filteredStaff.length}</strong> staff · {totalDays} calendar days · {monthTitle}</p></div>
                                            <div className="staff-table-tools">
                                                <label className="staff-view-select"><i className="ri-filter-line" /><select value={attendanceView} onChange={(e) => setAttendanceView(e.target.value)}><option value="all">All Staff</option><option value="low">Below 75%</option><option value="absent">Has Absence</option><option value="not_marked">Has Not Marked</option><option value="perfect">100% Attendance</option></select></label>
                                                <label className="staff-search"><i className="ri-search-line" /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff name, code, mobile..." />{search && <button type="button" onClick={() => setSearch("")} aria-label="Clear search"><i className="ri-close-line" /></button>}</label>
                                            </div>
                                        </div>

                                        <div className="staff-legend staff-no-print">
                                            <span><b className="legend-present">P / In-Out</b> Present</span>
                                            <span><b className="legend-absent">A</b> Absent</span>
                                            <span><b className="legend-holiday">H</b> Holiday / Non-working day</span>
                                            <span><b className="legend-na">NA</b> Before joining</span>
                                            <span><b className="legend-not">N</b> Not Marked</span>
                                        </div>

                                        <div id="PrintDiv" className="staff-print-area">
                                            <div className="staff-print-title">
                                                <h2>Monthly Staff Attendance Register</h2>
                                                <p>{monthTitle}</p>
                                                <small>P: Present · A: Absent · H: Holiday / Non-working · NA: Before joining · N: Not Marked</small>
                                            </div>

                                            <div className="staff-table-scroll">
                                                <table className="staff-monthly-table">
                                                    <thead>
                                                        <tr>
                                                            <th className="staff-serial-col">#</th>
                                                            <th className="staff-name-col">Staff Name / Code</th>
                                                            {days.map((day) => {
                                                                const info = monthDayInfo[day];
                                                                return (
                                                                    <th key={day} className={`staff-day-col ${info?.isHoliday ? "holiday-day" : ""}`}>
                                                                        <strong>{day}</strong>
                                                                        <small>{info?.dateObject?.toLocaleDateString("en-IN", { weekday: "short" })?.slice(0, 2)}</small>
                                                                    </th>
                                                                );
                                                            })}
                                                            <th className="staff-total-col">P</th>
                                                            <th className="staff-total-col">A</th>
                                                            <th className="staff-total-col">N</th>
                                                            <th className="staff-total-col">Working</th>
                                                            <th className="staff-total-col">%</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {!loading && filteredStaff.length === 0 && (
                                                            <tr><td colSpan={totalDays + 7} className="staff-empty-cell"><i className="ri-calendar-close-line" /><strong>No Attendance Data Found</strong><span>No staff attendance found for the selected month or filters.</span></td></tr>
                                                        )}

                                                        {filteredStaff.map((row, index) => {
                                                            const staff = row.staff;
                                                            return (
                                                                <tr key={staff?.id ?? index} className={row.workingDays > 0 && row.percentage < 75 ? "low-attendance-row" : ""}>
                                                                    <td className="staff-serial-col">{index + 1}</td>
                                                                    <td className="staff-name-col">
                                                                        <div className="staff-person">
                                                                            <span className="staff-avatar">{getInitials(staff?.name)}</span>
                                                                            <span className="staff-person-detail">
                                                                                <strong>{staff?.name || "—"}</strong>
                                                                                <small>{staff?.staff_code || staff?.employee_id || staff?.employment_latest?.designation?.name || staff?.gender || ""}</small>
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    {days.map((day) => {
                                                                        const info = row.dayStatuses[day];
                                                                        return <td key={day} className={`staff-day-cell ${info?.isHoliday ? "holiday-cell" : ""}`}>{renderDayCell(info)}</td>;
                                                                    })}
                                                                    <td className="staff-total-cell present-total">{row.present}</td>
                                                                    <td className="staff-total-cell absent-total">{row.absent}</td>
                                                                    <td className="staff-total-cell pending-total">{row.notMarked}</td>
                                                                    <td className="staff-total-cell">{row.workingDays}</td>
                                                                    <td className="staff-total-cell"><span className={`staff-percent ${row.percentage >= 90 ? "excellent" : row.percentage >= 75 ? "good" : "low"}`}>{row.percentage}%</span></td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    {filteredStaff.length > 0 && (
                                                        <tfoot>
                                                            <tr className="staff-grand-total">
                                                                <th colSpan="2">MONTHLY TOTAL</th>
                                                                {days.map((day) => {
                                                                    const dayRows = filteredStaff.map((row) => row.dayStatuses[day]);
                                                                    const present = dayRows.filter((info) => info?.status === "present").length;
                                                                    const absent = dayRows.filter((info) => info?.status === "absent").length;
                                                                    const notMarked = dayRows.filter((info) => info?.status === "not_marked").length;
                                                                    return <td key={day} title={`Present: ${present}, Absent: ${absent}, Not marked: ${notMarked}`}><span className="staff-footer-day"><b>{present}</b><small>P</small></span><span className="staff-footer-day"><b>{absent}</b><small>A</small></span><span className="staff-footer-day"><b>{notMarked}</b><small>N</small></span></td>;
                                                                })}
                                                                <td>{summary.present}</td>
                                                                <td>{summary.absent}</td>
                                                                <td>{summary.notMarked}</td>
                                                                <td>{summary.workingDays}</td>
                                                                <td>{summary.percentage}%</td>
                                                            </tr>
                                                        </tfoot>
                                                    )}
                                                </table>
                                            </div>
                                        </div>

                                        <div className="staff-table-footer staff-no-print"><span><i className="ri-information-line" /> All staff are shown in rows; dates are shown across the top. Scroll horizontally to view all days and monthly totals.</span><strong>{monthTitle}</strong></div>
                                    </section>
                                </main>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .staff-monthly-page { --staff-ink:#172033; --staff-muted:#748094; --staff-border:#e5eaf1; --staff-primary:#3563e9; color:var(--staff-ink); }
                .staff-monthly-container { max-width:1800px; }
                .staff-monthly-main { display:flex; flex-direction:column; gap:20px; }
                .staff-monthly-header { display:flex; justify-content:space-between; align-items:center; gap:18px; padding:24px 26px; border:1px solid #e7ecf5; border-radius:18px; background:linear-gradient(115deg,#fff 0%,#f4f7ff 100%); box-shadow:0 8px 28px rgba(30,55,100,.05); }
                .staff-monthly-heading { display:flex; align-items:center; gap:16px; }
                .staff-monthly-icon { display:grid; place-items:center; flex:0 0 58px; width:58px; height:58px; border-radius:17px; background:#e9efff; color:#3563e9; font-size:27px; }
                .staff-breadcrumb { display:flex; align-items:center; gap:6px; margin-bottom:5px; color:#778399; font-size:12px; font-weight:700; }
                .staff-monthly-heading h1 { margin:0; color:#18243a; font-size:clamp(21px,2vw,29px); font-weight:800; letter-spacing:-.5px; }
                .staff-monthly-heading p { margin:7px 0 0; color:#758197; font-size:13px; }
                .staff-header-actions { display:flex; align-items:center; gap:9px; flex-wrap:wrap; }
                .staff-action { display:inline-flex; align-items:center; justify-content:center; gap:8px; border:1px solid transparent; border-radius:10px; padding:11px 15px; font-weight:700; font-size:13px; transition:.18s ease; white-space:nowrap; cursor:pointer; }
                .staff-action:disabled { opacity:.5; cursor:not-allowed; }
                .staff-action.primary { color:#fff; background:#3563e9; box-shadow:0 5px 12px #3563e92b; }
                .staff-action.primary:hover:not(:disabled) { background:#244fd0; }
                .staff-action.secondary { color:#34415a; border-color:#dfe5ef; background:#fff; }
                .staff-filter-card,.staff-register-card { overflow:hidden; border:1px solid var(--staff-border); border-radius:16px; background:#fff; box-shadow:0 5px 20px rgba(30,55,100,.035); }
                .staff-filter-card { padding:21px 23px; }
                .staff-section-title { display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:18px; }
                .staff-section-title h2,.staff-table-heading h2 { margin:0; font-size:16px; font-weight:800; color:#202d43; }
                .staff-section-title h2 i { margin-right:7px; color:#3563e9; }
                .staff-section-title p,.staff-table-heading p { margin:5px 0 0; color:#8390a3; font-size:12px; }
                .staff-reset { display:inline-flex; align-items:center; gap:6px; border:1px solid #e3e8f1; border-radius:9px; padding:8px 11px; background:#fff; color:#56637a; font-size:12px; font-weight:700; cursor:pointer; }
                .staff-filter-grid { display:grid; grid-template-columns:minmax(220px,310px) 1fr; gap:15px; align-items:end; }
                .staff-field { display:flex; flex-direction:column; gap:7px; color:#5c6880; font-size:12px; font-weight:700; }
                .staff-input-wrap { position:relative; }
                .staff-input-wrap>i { position:absolute; z-index:1; top:50%; left:12px; transform:translateY(-50%); color:#8a96aa; font-size:17px; }
                .staff-input-wrap input { width:100%; min-height:43px; padding:9px 12px 9px 38px; border:1px solid #dfe5ef; border-radius:10px; background:#fff; color:#26334a; outline:none; }
                .staff-input-wrap input:focus,.staff-search input:focus,.staff-view-select select:focus { border-color:#91a9ff; box-shadow:0 0 0 3px #3563e914; }
                .staff-filter-note { display:flex; align-items:center; gap:8px; min-height:43px; padding:10px 13px; border:1px dashed #dbe3f3; border-radius:10px; background:#f8faff; color:#68768d; font-size:12px; }
                .staff-filter-note i { color:#3563e9; font-size:17px; }
                .staff-summary-grid { display:grid; grid-template-columns:repeat(5,minmax(0,1fr)); gap:13px; }
                .staff-summary-card { min-width:0; padding:17px 18px; border:1px solid var(--staff-border); border-radius:15px; background:#fff; box-shadow:0 4px 16px rgba(30,55,100,.025); }
                .staff-summary-top { display:flex; align-items:center; gap:9px; }
                .staff-summary-top>span { display:grid; place-items:center; width:35px; height:35px; border-radius:10px; font-size:18px; }
                .staff-summary-top small { color:#7a879c; font-size:12px; font-weight:700; }
                .staff-summary-card>strong { display:block; margin-top:12px; font-size:29px; line-height:1; letter-spacing:-.7px; color:#202c42; }
                .staff-summary-card p { margin:8px 0 0; color:#8b96a8; font-size:11px; }
                .staff-summary-card.blue .staff-summary-top>span { color:#3563e9; background:#edf1ff; }
                .staff-summary-card.green .staff-summary-top>span { color:#15956b; background:#e7f8f1; }
                .staff-summary-card.red .staff-summary-top>span { color:#d94b58; background:#fff0f0; }
                .staff-summary-card.amber .staff-summary-top>span { color:#c18418; background:#fff6e4; }
                .staff-summary-card.violet .staff-summary-top>span { color:#8257d8; background:#f2ecff; }
                .staff-table-heading { display:flex; justify-content:space-between; align-items:center; gap:15px; padding:20px 22px 16px; border-bottom:1px solid #edf0f5; }
                .staff-table-tools { display:flex; justify-content:flex-end; align-items:center; gap:9px; flex-wrap:wrap; }
                .staff-view-select,.staff-search { display:flex; align-items:center; gap:7px; min-height:39px; border:1px solid #e0e6ef; border-radius:9px; background:#fff; padding:0 10px; color:#7d899d; }
                .staff-view-select select,.staff-search input { border:0; outline:0; background:transparent; color:#4a5870; font-size:12px; min-height:35px; }
                .staff-view-select select { max-width:165px; }
                .staff-search input { width:220px; }
                .staff-search button { border:0; background:transparent; color:#78859a; padding:2px; cursor:pointer; }
                .staff-legend { display:flex; align-items:center; gap:16px; flex-wrap:wrap; padding:12px 22px; border-bottom:1px solid #edf0f5; background:#fbfcfe; color:#738096; font-size:11px; }
                .staff-legend b { display:inline-grid; place-items:center; min-width:22px; margin-right:4px; font-size:10px; }
                .legend-present { color:#1674c4; }.legend-absent { color:#d64552; }.legend-holiday,.legend-na { color:#8994a5; }.legend-not { color:#c18418; }
                .staff-print-title { display:none; }
                .staff-table-scroll { width:100%; overflow:auto; }
                .staff-monthly-table { width:max-content; min-width:100%; border-collapse:separate; border-spacing:0; font-size:11px; }
                .staff-monthly-table th,.staff-monthly-table td { padding:8px 5px; border-right:1px solid #e9edf4; border-bottom:1px solid #e9edf4; text-align:center; vertical-align:middle; }
                .staff-monthly-table thead th { position:sticky; top:0; z-index:3; background:#f2f5fb; color:#4d5b73; font-size:11px; font-weight:800; }
                .staff-monthly-table thead th strong { display:block; color:#35425a; }
                .staff-monthly-table thead th small { display:block; margin-top:3px; color:#8a95a7; font-size:9px; font-weight:500; }
                .staff-monthly-table th:first-child,.staff-monthly-table td:first-child { border-left:1px solid #e9edf4; }
                .staff-monthly-table .staff-serial-col { width:42px; min-width:42px; }
                .staff-monthly-table .staff-name-col { min-width:180px; width:180px; text-align:left; }
                .staff-monthly-table .staff-day-col { width:48px; min-width:48px; }
                .staff-monthly-table .staff-total-col { min-width:58px; background:#f4f6fb; }
                .staff-monthly-table tbody tr:nth-child(even) td { background:#fcfdff; }
                .staff-monthly-table tbody tr:hover td { background:#f6f8ff; }
                .staff-person { display:flex; align-items:center; gap:9px; min-width:0; text-align:left; }
                .staff-avatar { display:grid; place-items:center; flex:0 0 32px; width:32px; height:32px; border-radius:10px; background:#eaf0ff; color:#3563e9; font-size:10px; font-weight:800; }
                .staff-person-detail { display:flex; flex-direction:column; gap:3px; min-width:0; }
                .staff-person-detail strong { color:#29364d; font-size:11px; overflow-wrap:anywhere; }
                .staff-person-detail small { color:#8b96a8; font-size:9px; }
                .staff-day-cell { min-width:48px; }
                .staff-day-times { display:flex; flex-direction:column; align-items:center; gap:2px; white-space:nowrap; }
                .staff-in-time { color:#2472ba; font-size:10px; font-weight:700; }
                .staff-out-time { color:#16835f; font-size:10px; font-weight:700; }
                .staff-status { display:inline-flex; align-items:center; justify-content:center; min-width:21px; height:21px; padding:0 3px; border-radius:6px; font-size:10px; font-weight:800; }
                .staff-status.present { color:#1674c4; background:#e9f4ff; }
                .staff-status.absent { color:#d74350; background:#fff0f0; }
                .staff-status.not-marked { color:#b47a17; background:#fff7e7; }
                .staff-status.holiday,.staff-status.na { color:#8a94a4; background:#f0f2f6; }
                .staff-future { color:#d5dae3; }
                .staff-monthly-table .holiday-cell,.staff-monthly-table .holiday-day { background:#fafbfc !important; }
                .staff-total-cell { min-width:58px; font-weight:800; color:#3f4d65; background:#fbfcff; }
                .present-total { color:#16835f; }.absent-total { color:#d74350; }.pending-total { color:#b47a17; }
                .staff-percent { display:inline-block; padding:4px 6px; border-radius:7px; font-weight:800; }
                .staff-percent.excellent { color:#12805c; background:#e5f8f0; }.staff-percent.good { color:#2865be; background:#eaf2ff; }.staff-percent.low { color:#c13f4c; background:#fff0f0; }
                .low-attendance-row td { background:#fffafa; }
                .staff-grand-total th,.staff-grand-total td { position:static; padding:8px 4px; background:#eaf0fb !important; color:#263750; font-weight:800; border-top:2px solid #aebcda; }
                .staff-grand-total th { text-align:left; white-space:nowrap; }
                .staff-footer-day { display:inline-flex; gap:2px; align-items:center; margin:0 2px; font-size:9px; }
                .staff-footer-day small { font-size:8px; color:#7b879a; }
                .staff-empty-cell { padding:45px 20px !important; color:#8b96a8; text-align:center !important; }
                .staff-empty-cell i,.staff-empty-cell strong,.staff-empty-cell span { display:block; margin:5px auto; }
                .staff-empty-cell i { font-size:30px; color:#b1bdd0; }.staff-empty-cell strong { color:#53617a; }.staff-empty-cell span { font-size:12px; }
                .staff-table-footer { display:flex; justify-content:space-between; gap:12px; padding:13px 22px; border-top:1px solid #edf0f5; color:#8490a3; font-size:11px; }
                .staff-table-footer span { display:flex; align-items:center; gap:6px; }
                @media (max-width:1200px) { .staff-summary-grid { grid-template-columns:repeat(3,minmax(0,1fr)); } .staff-monthly-header { align-items:flex-start; flex-direction:column; } }
                @media (max-width:768px) { .staff-monthly-main { gap:13px; }.staff-monthly-header { padding:18px; }.staff-monthly-heading { align-items:flex-start; }.staff-monthly-icon { width:44px;height:44px;flex-basis:44px;font-size:22px;border-radius:13px; }.staff-monthly-heading p { line-height:1.5; }.staff-filter-card { padding:16px; }.staff-filter-grid { grid-template-columns:1fr; }.staff-summary-grid { grid-template-columns:repeat(2,minmax(0,1fr)); gap:9px; }.staff-summary-card { padding:13px; }.staff-summary-card>strong { font-size:25px; }.staff-table-heading { align-items:flex-start; flex-direction:column; padding:16px; }.staff-table-tools { width:100%; justify-content:flex-start; }.staff-search { flex:1; min-width:0; }.staff-search input { width:100%; min-width:0; }.staff-legend { padding:11px 16px; gap:10px; }.staff-table-footer { padding:12px 16px; flex-direction:column; } }
                @page { size:A4 landscape; margin:6mm; }
                @media print {
                    html,body { width:100% !important; margin:0 !important; padding:0 !important; background:#fff !important; color:#111 !important; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
                    .staff-monthly-page { width:100% !important; background:#fff !important; }
                    .staff-monthly-container { max-width:none !important; padding:0 !important; margin:0 !important; }
                    .staff-no-print,.staff-monthly-page .layout-container>.staff-no-print,.staff-monthly-page .layout-page>.staff-no-print { display:none !important; }
                    .staff-monthly-page .content-wrapper { padding:0 !important; margin:0 !important; }
                    .staff-monthly-main { display:block !important; }
                    .staff-summary-grid { display:none !important; }
                    .staff-register-card { border:0 !important; border-radius:0 !important; box-shadow:none !important; overflow:visible !important; }
                    .staff-print-title { display:block !important; text-align:center; padding:0 0 7px; border-bottom:1.5px solid #333; margin-bottom:7px; }
                    .staff-print-title h2 { margin:0 0 3px !important; font-size:14px !important; color:#111 !important; font-weight:800 !important; }
                    .staff-print-title p { margin:0 0 3px !important; font-size:11px !important; font-weight:700 !important; }
                    .staff-print-title small { font-size:7px !important; }
                    .staff-table-scroll { overflow:visible !important; width:100% !important; }
                    .staff-monthly-table { width:100% !important; min-width:0 !important; table-layout:fixed !important; border-collapse:collapse !important; font-size:6.5px !important; }
                    .staff-monthly-table th,.staff-monthly-table td { position:static !important; border:1px solid #555 !important; padding:2px 1px !important; color:#111 !important; font-size:6.5px !important; line-height:1.1 !important; overflow-wrap:anywhere !important; }
                    .staff-monthly-table thead th { background:#e9eef5 !important; }
                    .staff-monthly-table thead th strong { font-size:7px !important; }
                    .staff-monthly-table thead th small { font-size:6px !important; }
                    .staff-monthly-table .staff-serial-col { width:20px !important; min-width:20px !important; }
                    .staff-monthly-table .staff-name-col { width:105px !important; min-width:105px !important; }
                    .staff-monthly-table .staff-day-col { width:auto !important; min-width:0 !important; }
                    .staff-monthly-table .staff-total-col,.staff-total-cell { min-width:0 !important; width:28px !important; }
                    .staff-avatar { display:none !important; }
                    .staff-person { display:block !important; }
                    .staff-person-detail strong { font-size:6.5px !important; }
                    .staff-person-detail small { font-size:5.5px !important; }
                    .staff-day-times { gap:1px !important; }
                    .staff-in-time,.staff-out-time,.staff-status { font-size:5.5px !important; }
                    .staff-status { min-width:0 !important; height:auto !important; padding:0 !important; border-radius:0 !important; background:transparent !important; }
                    .staff-percent { padding:0 !important; background:transparent !important; }
                    .staff-grand-total th,.staff-grand-total td { background:#e8e8e8 !important; }
                    .staff-monthly-table thead { display:table-header-group; }
                    .staff-monthly-table tfoot { display:table-row-group; }
                    .staff-monthly-table tr { break-inside:avoid; page-break-inside:avoid; }
                    .staff-table-heading,.staff-legend,.staff-table-footer { display:none !important; }
                }
            `}</style>
        </>
    );
}
